import { useQuery } from "@apollo/client";
import { Alert, Box } from "@mui/material";
import { useEffect, useRef } from "react";
import { useCanAccess, useTranslate } from "react-admin";

import { ResourceName } from "../../constants/resources";
import {
  LogisticsOrderCommandState,
  LogisticsOrderCommandStatusDocument,
  type LogisticsOrderCommandStatusQuery,
  type LogisticsOrderCommandStatusQueryVariables,
} from "../../generated/graphql";

const POLL_INTERVAL_MS = 2000;

export const LogisticsOrderCommandStatus = ({
  salesforceId,
  onSynced,
}: {
  salesforceId: string;
  onSynced: () => unknown;
}) => {
  const translate = useTranslate();
  const { canAccess } = useCanAccess({ action: "list", resource: ResourceName.LogisticsInventory });
  const { data, error, startPolling, stopPolling } = useQuery<
    LogisticsOrderCommandStatusQuery,
    LogisticsOrderCommandStatusQueryVariables
  >(LogisticsOrderCommandStatusDocument, {
    variables: { salesforceId },
    skip: canAccess !== true,
    fetchPolicy: "network-only",
  });
  const status = data?.logisticsOrderCommandStatus;
  const pending = useRef(new Set<string>());
  const refreshed = useRef(new Set<string>());

  useEffect(() => {
    if (canAccess === true && status?.state === LogisticsOrderCommandState.Updating) {
      startPolling(POLL_INTERVAL_MS);
    } else {
      stopPolling();
    }
    return () => stopPolling();
  }, [canAccess, status?.state, startPolling, stopPolling]);

  useEffect(() => {
    if (canAccess !== true || !status) return;
    if (status.state === LogisticsOrderCommandState.Updating) pending.current.add(status.commandId);
    if (
      status.state === LogisticsOrderCommandState.Synced &&
      pending.current.has(status.commandId) &&
      !refreshed.current.has(status.commandId)
    ) {
      refreshed.current.add(status.commandId);
      void onSynced();
    }
  }, [canAccess, status, onSynced]);

  if (canAccess !== true) return null;
  if (error) return <Alert severity="warning">{translate("logisticsCommandStatus.loadError")}</Alert>;
  if (!status) return null;
  const severity =
    status.state === LogisticsOrderCommandState.Synced
      ? "success"
      : status.state === LogisticsOrderCommandState.Updating
        ? "info"
        : "warning";
  return (
    <Alert severity={severity} sx={{ mb: "12px" }} data-testid="logistics-order-command-status">
      {translate(`logisticsCommandStatus.states.${status.state}`)}
      {status.refusal && (
        <Box sx={{ mt: "4px" }}>
          {translate(`logisticsCommandStatus.refusals.${status.refusal}`, {
            _: translate("logisticsCommandStatus.refusalFallback"),
          })}
        </Box>
      )}
      {status.messages.map((message, index) => (
        <Box key={index} sx={{ mt: "4px", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
          {message}
        </Box>
      ))}
    </Alert>
  );
};
